/**
 * Prior Care Plan Context Component (DRR-220 Phase 5)
 *
 * Displays prior care plan information during 90-day reassessment.
 * Used by both LPN Assessment and DoN Review pages.
 *
 * MN 144G.70: Care plans must be reviewed at least every 90 days.
 * This component shows:
 * - Days until expiry
 * - Prior ADL levels
 * - Changes since last assessment
 * - Clinical observations history
 */

import { useMemo } from 'react';
import {
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Alert,
  SimpleGrid,
  ThemeIcon,
  Progress,
  Accordion,
  Table,
  Tooltip,
  Divider,
} from '@mantine/core';
import {
  IconHistory,
  IconArrowRight,
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconUser,
  IconBath,
  IconWalk,
  IconClipboardCheck,
  IconTarget,
} from '@tabler/icons-react';
// eslint-disable-next-line no-restricted-imports -- Data layer hook requires direct Apollo access
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// =============================================================================
// GraphQL
// =============================================================================

const GET_CARE_PLAN_COMPARISON = gql`
  query GetCarePlanComparison($residentActorId: String!, $generationId: String) {
    carePlanComparison(residentActorId: $residentActorId, generationId: $generationId) {
      residentActorId
      priorPlan {
        carePlanId
        finalizedAt
        daysSinceFinalized
        isCurrent
        adlSummary
        clinicalObservations
        goals
        approvedBy
      }
      changes {
        field
        previousValue
        newValue
        reason
      }
      isInitialAssessment
      daysUntilExpiry
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

interface PriorCarePlanContextProps {
  residentActorId: string;
  generationId?: string;
  /** Whether to show the full comparison or just the summary */
  mode?: 'summary' | 'full';
}

interface AdlLevel {
  bathing?: string;
  dressing?: string;
  mobility?: string;
  toileting?: string;
  eating?: string;
  [key: string]: string | undefined;
}

// =============================================================================
// ADL Icons
// =============================================================================

const ADL_ICONS: Record<string, React.ReactNode> = {
  bathing: <IconBath size={16} />,
  mobility: <IconWalk size={16} />,
  dressing: <IconUser size={16} />,
  toileting: <IconClipboardCheck size={16} />,
  eating: <IconTarget size={16} />,
};

// =============================================================================
// Expiry Badge
// =============================================================================

function ExpiryBadge({ daysUntilExpiry }: { daysUntilExpiry: number | null }) {
  if (daysUntilExpiry === null) return null;

  if (daysUntilExpiry < 0) {
    return (
      <Badge color="red" variant="filled" leftSection={<IconAlertTriangle size={12} />}>
        Expired {Math.abs(daysUntilExpiry)} days ago
      </Badge>
    );
  }

  if (daysUntilExpiry <= 14) {
    return (
      <Badge color="orange" variant="filled" leftSection={<IconClock size={12} />}>
        Expires in {daysUntilExpiry} days
      </Badge>
    );
  }

  return (
    <Badge color="green" variant="light" leftSection={<IconCheck size={12} />}>
      Valid for {daysUntilExpiry} more days
    </Badge>
  );
}

// =============================================================================
// ADL Level Display
// =============================================================================

function AdlLevelCard({ label, level }: { label: string; level: string }) {
  const colorMap: Record<string, string> = {
    independent: 'green',
    supervision: 'blue',
    limited: 'yellow',
    extensive: 'orange',
    total: 'red',
    independent_device: 'teal',
  };

  const normalizedLevel = level.toLowerCase().replace(/ /g, '_');
  const color = colorMap[normalizedLevel] ?? 'gray';

  return (
    <Paper p="xs" withBorder>
      <Group gap="xs">
        <ThemeIcon size="sm" variant="light" color={color}>
          {ADL_ICONS[label.toLowerCase()] ?? <IconUser size={14} />}
        </ThemeIcon>
        <div>
          <Text size="xs" c="dimmed" tt="capitalize">{label}</Text>
          <Text size="sm" fw={500} tt="capitalize">{level.replace(/_/g, ' ')}</Text>
        </div>
      </Group>
    </Paper>
  );
}

// =============================================================================
// Change Row
// =============================================================================

function ChangeRow({
  field,
  previousValue,
  newValue,
  reason,
}: {
  field: string;
  previousValue: string;
  newValue: string;
  reason?: string | null;
}) {
  return (
    <Table.Tr>
      <Table.Td>
        <Text fw={500} tt="capitalize">{field.replace(/_/g, ' ')}</Text>
      </Table.Td>
      <Table.Td>
        <Badge variant="light" color="gray">{previousValue}</Badge>
      </Table.Td>
      <Table.Td>
        <ThemeIcon size="sm" variant="transparent" c="dimmed">
          <IconArrowRight size={14} />
        </ThemeIcon>
      </Table.Td>
      <Table.Td>
        <Badge variant="filled" color="blue">{newValue}</Badge>
      </Table.Td>
      <Table.Td>
        {reason && (
          <Tooltip label={reason} multiline maw={300}>
            <Text size="xs" c="dimmed" lineClamp={1}>{reason}</Text>
          </Tooltip>
        )}
      </Table.Td>
    </Table.Tr>
  );
}

// =============================================================================
// Main Component
// =============================================================================

export function PriorCarePlanContext({
  residentActorId,
  generationId,
  mode = 'full',
}: PriorCarePlanContextProps) {
  const { data, loading, error } = useQuery(GET_CARE_PLAN_COMPARISON, {
    variables: { residentActorId, generationId },
    skip: !residentActorId,
  });

  const comparison = data?.carePlanComparison;

  // Parse ADL summary
  const priorAdlLevels: AdlLevel = useMemo(() => {
    if (!comparison?.priorPlan?.adlSummary) return {};
    try {
      return JSON.parse(comparison.priorPlan.adlSummary);
    } catch {
      return {};
    }
  }, [comparison?.priorPlan?.adlSummary]);

  // Initial assessment - no prior plan
  if (!loading && comparison?.isInitialAssessment) {
    return (
      <Alert color="blue" title="Initial Assessment" icon={<IconUser />}>
        <Text size="sm">
          This is the first care plan for this resident. No prior plan exists for comparison.
        </Text>
      </Alert>
    );
  }

  // Loading/error states
  if (loading) {
    return (
      <Paper p="md" withBorder>
        <Text c="dimmed">Loading prior care plan...</Text>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert color="red" title="Error">
        Failed to load prior care plan: {error.message}
      </Alert>
    );
  }

  if (!comparison?.priorPlan) {
    return null;
  }

  const { priorPlan, changes, daysUntilExpiry } = comparison;

  // Summary mode - compact display
  if (mode === 'summary') {
    return (
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Group gap="xs">
            <ThemeIcon size="sm" variant="light" color="violet">
              <IconHistory size={14} />
            </ThemeIcon>
            <Text fw={500} size="sm">Prior Care Plan</Text>
          </Group>
          <ExpiryBadge daysUntilExpiry={daysUntilExpiry ?? null} />
        </Group>

        <Text size="xs" c="dimmed" mb="xs">
          Finalized {priorPlan.daysSinceFinalized} days ago by {priorPlan.approvedBy}
        </Text>

        {changes.length > 0 && (
          <Badge color="orange" variant="light" size="sm">
            {changes.length} change{changes.length !== 1 ? 's' : ''} detected
          </Badge>
        )}
      </Paper>
    );
  }

  // Full mode - detailed display
  return (
    <Stack gap="md">
      {/* Header with expiry status */}
      <Paper p="md" withBorder>
        <Group justify="space-between" mb="md">
          <Group gap="sm">
            <ThemeIcon size="lg" variant="light" color="violet">
              <IconHistory size={20} />
            </ThemeIcon>
            <div>
              <Title order={4}>Prior Care Plan</Title>
              <Text size="sm" c="dimmed">
                ID: {priorPlan.carePlanId}
              </Text>
            </div>
          </Group>
          <ExpiryBadge daysUntilExpiry={daysUntilExpiry ?? null} />
        </Group>

        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="xs">
          <Paper p="xs" bg="gray.0">
            <Text size="xs" c="dimmed">Finalized</Text>
            <Text fw={500}>{priorPlan.daysSinceFinalized} days ago</Text>
          </Paper>
          <Paper p="xs" bg="gray.0">
            <Text size="xs" c="dimmed">Approved By</Text>
            <Text fw={500}>{priorPlan.approvedBy}</Text>
          </Paper>
          <Paper p="xs" bg="gray.0">
            <Text size="xs" c="dimmed">Status</Text>
            <Text fw={500}>{priorPlan.isCurrent ? 'Current' : 'Expired'}</Text>
          </Paper>
          <Paper p="xs" bg="gray.0">
            <Text size="xs" c="dimmed">Changes</Text>
            <Text fw={500} c={changes.length > 0 ? 'orange' : 'green'}>
              {changes.length} detected
            </Text>
          </Paper>
        </SimpleGrid>
      </Paper>

      {/* ADL Levels */}
      <Accordion variant="contained" defaultValue="adl">
        <Accordion.Item value="adl">
          <Accordion.Control icon={<IconClipboardCheck size={16} />}>
            Prior ADL Levels
          </Accordion.Control>
          <Accordion.Panel>
            <SimpleGrid cols={{ base: 2, md: 5 }} spacing="xs">
              {Object.entries(priorAdlLevels).map(([key, value]) => (
                <AdlLevelCard key={key} label={key} level={value ?? 'Unknown'} />
              ))}
            </SimpleGrid>
          </Accordion.Panel>
        </Accordion.Item>

        {/* Changes */}
        {changes.length > 0 && (
          <Accordion.Item value="changes">
            <Accordion.Control icon={<IconArrowRight size={16} />}>
              <Group gap="xs">
                <Text>Changes Since Last Assessment</Text>
                <Badge color="orange" size="sm">{changes.length}</Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Field</Table.Th>
                    <Table.Th>Previous</Table.Th>
                    <Table.Th></Table.Th>
                    <Table.Th>New</Table.Th>
                    <Table.Th>Reason</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {changes.map((change: any, idx: number) => (
                    <ChangeRow
                      key={idx}
                      field={change.field}
                      previousValue={change.previousValue}
                      newValue={change.newValue}
                      reason={change.reason}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* Clinical Observations */}
        <Accordion.Item value="observations">
          <Accordion.Control icon={<IconTarget size={16} />}>
            Prior Clinical Observations
          </Accordion.Control>
          <Accordion.Panel>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {priorPlan.clinicalObservations}
            </Text>
          </Accordion.Panel>
        </Accordion.Item>

        {/* Goals */}
        <Accordion.Item value="goals">
          <Accordion.Control icon={<IconCheck size={16} />}>
            Prior Goals ({priorPlan.goals?.length ?? 0})
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="xs">
              {priorPlan.goals?.map((goal: string, idx: number) => (
                <Group key={idx} gap="xs">
                  <ThemeIcon size="xs" variant="light" color="green">
                    <IconCheck size={12} />
                  </ThemeIcon>
                  <Text size="sm">{goal}</Text>
                </Group>
              ))}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>
      </Accordion>
    </Stack>
  );
}

export default PriorCarePlanContext;
